import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { institutionLogoUrl, uploadInstituteLogo } from '../../shared/lib/institute-branding';

import { updateInstituteLogo } from './api';
import s from './InstituteLogoControl.module.css';

interface InstituteLogoControlProps {
  instituteId: string;
  logoPath: string | null;
  canEdit: boolean;
  onSaved: (logoPath: string | null) => void;
}

/**
 * Uploads and persists in one step — there is no larger form this belongs to
 * (unlike the feature toggles above it on the Settings page), so waiting for
 * a separate "Save" click would just be a place to lose an upload.
 */
export function InstituteLogoControl({
  instituteId,
  logoPath,
  canEdit,
  onSaved,
}: InstituteLogoControlProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (file: File | null) => {
      const path = file === null ? null : await uploadInstituteLogo(instituteId, file);
      return updateInstituteLogo(instituteId, path);
    },
    onSuccess: (result) => {
      setError(null);
      onSaved(result.logo_path);
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const preview = institutionLogoUrl(logoPath);

  return (
    <div className={s.root}>
      {preview !== null ? (
        <img className={s.thumb} src={preview} alt="Institute logo" width={160} height={64} />
      ) : (
        <div className={s.placeholder} aria-hidden="true">
          No logo
        </div>
      )}

      <div className={s.controls}>
        <label className={s.attachLabel} data-disabled={!canEdit || save.isPending}>
          <span>{logoPath !== null ? 'Replace logo' : 'Upload logo'}</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className={s.fileInput}
            disabled={!canEdit || save.isPending}
            aria-label={logoPath !== null ? 'Replace institute logo' : 'Upload institute logo'}
            onChange={(e) => {
              const file = e.target.files?.item(0);
              e.target.value = '';
              if (file) {
                setError(null);
                save.mutate(file);
              }
            }}
          />
        </label>
        {logoPath !== null ? (
          <button
            type="button"
            className={s.removeButton}
            disabled={!canEdit || save.isPending}
            onClick={() => {
              setError(null);
              save.mutate(null);
            }}
          >
            Remove logo
          </button>
        ) : null}
        {save.isPending ? (
          <span className={s.status} role="status">
            {save.variables === null ? 'Removing…' : 'Uploading…'}
          </span>
        ) : null}
        {error !== null ? (
          <span className={s.error} role="alert">
            {error}
          </span>
        ) : null}
        <p className={s.hint}>PNG, JPEG or WebP, up to 2 MB. Shown on every printed bill.</p>
      </div>
    </div>
  );
}
